import { Component, defineElement } from '@theme/component';

class YiseCrossSell extends Component {
  ready() {
    if (!window.YISETemporarySalePrice && document.readyState !== 'complete') {
      this.listen(document, 'DOMContentLoaded', () => this.initialize(), { once: true });
      this.listen(window, 'load', () => this.initialize(), { once: true });
      return;
    }
    this.initialize();
  }

  initialize() {
    if (this.initialized || !this.isConnected) return;
    const crossSell = this.querySelector('[data-cross-sell-root]');
    const configNode = this.querySelector('[data-cross-sell-config]');
    let config;
    try { config = JSON.parse(configNode?.textContent || ''); } catch {
      this.waitForMarkup();
      return;
    }
    if (!crossSell || !config || typeof config !== 'object' || Array.isArray(config)) {
      this.waitForMarkup();
      return;
    }
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.initialized = true;
    const owner = this;

    // Tab switching (only if tabs exist)
    const tabs = crossSell.querySelectorAll('.pdp-cross-sell__tab');
    const panels = crossSell.querySelectorAll('.pdp-cross-sell__panel');
    const isSingleMode = crossSell.classList.contains('pdp-cross-sell--single');

    if (!isSingleMode && tabs.length > 0) {
      const activateTab = (tab) => {
        const targetPanel = tab.getAttribute('data-tab');

        tabs.forEach(t => {
          const isActive = t === tab;
          t.classList.toggle('pdp-cross-sell__tab--active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          t.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        panels.forEach(panel => {
          const isActive = panel.getAttribute('data-panel') === targetPanel;
          panel.classList.toggle('pdp-cross-sell__panel--active', isActive);
          panel.hidden = !isActive;
        });
      };

      tabs.forEach(tab => {
        owner.listen(tab, 'click', function () {
          activateTab(this);
        });

        owner.listen(tab, 'keydown', function (event) {
          const currentIndex = Array.from(tabs).indexOf(this);
          let nextIndex = currentIndex;

          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === currentIndex) return;

          event.preventDefault();
          activateTab(tabs[nextIndex]);
          tabs[nextIndex].focus();
        });
      });
    }

    // View more/less toggle
    const toggleButtons = crossSell.querySelectorAll('.text-button');

    toggleButtons.forEach(button => {
      owner.listen(button, 'click', function () {
        const panel = this.closest('.pdp-cross-sell__panel');
        const itemsContainer = panel.querySelector('.pdp-cross-sell__items');
        const items = panel.querySelectorAll('.pdp-cross-sell__item');
        const currentVisible = parseInt(itemsContainer.getAttribute('data-visible-items'));
        const isExpanded = this.getAttribute('data-action') === 'view-less';

        if (isExpanded) {
          // Collapse to show only first item
          items.forEach((item, index) => {
            if (index > 0) {
              item.classList.add('pdp-cross-sell__item--hidden');
            }
          });
          itemsContainer.setAttribute('data-visible-items', '1');
          this.setAttribute('data-action', 'view-more');
          this.setAttribute('aria-expanded', 'false');
          this.innerHTML =
            'VIEW MORE <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5"/></svg>';
        } else {
          // Expand to show all items
          items.forEach(item => {
            item.classList.remove('pdp-cross-sell__item--hidden');
          });
          itemsContainer.setAttribute('data-visible-items', items.length);
          this.setAttribute('data-action', 'view-less');
          this.setAttribute('aria-expanded', 'true');
          this.innerHTML =
            'VIEW LESS <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M10 8L6 4L2 8" stroke="currentColor" stroke-width="1.5"/></svg>';
        }
      });
    });

    // Accordion functionality
    const accordionTriggers = crossSell.querySelectorAll('.pdp-cross-sell__accordion-trigger');

    accordionTriggers.forEach(trigger => {
      owner.listen(trigger, 'click', function () {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        const content = crossSell.querySelector('#' + CSS.escape(this.getAttribute('aria-controls')));

        this.setAttribute('aria-expanded', !isExpanded);
        content.setAttribute('aria-hidden', isExpanded);

        if (!isExpanded) {
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          content.style.maxHeight = '0';
        }
      });
    });

    // Variant selection handling
    const variantDropdownContainers = crossSell.querySelectorAll('.pdp-cross-sell__variant-dropdowns');

    variantDropdownContainers.forEach(container => {
      const productId = container.getAttribute('data-product-id');
      const selects = container.querySelectorAll('.pdp-cross-sell__variant-select');
      const swatchInputs = container.querySelectorAll('.pdp-cross-sell__swatch-input');
      const swatchGroups = container.querySelectorAll('.pdp-cross-sell__swatch-group');
      const variantsDataScript = container.closest('.pdp-cross-sell__item').querySelector(`script[data-product-variants="${productId}"]`);
      
      if (!variantsDataScript) return;
      
      const variants = JSON.parse(variantsDataScript.textContent);
      const item = container.closest('.pdp-cross-sell__item');
      const form = item.querySelector('form');
      const variantIdInput = form.querySelector('input[name="id"]');
      const priceElement = form.querySelector('.js-price');
      const submitButton = form.querySelector('button[type="submit"]');
      const productImage = item.querySelector(`img[data-product-image="${productId}"]`);
      
      // Function to get selected options (combines swatches and dropdowns)
      function getSelectedOptions() {
        const options = [];
        
        // Get values from swatch groups
        swatchGroups.forEach(group => {
          const optionIndex = parseInt(group.getAttribute('data-option-index'));
          const checkedInput = group.querySelector('.pdp-cross-sell__swatch-input:checked');
          options[optionIndex] = checkedInput ? checkedInput.value : null;
        });
        
        // Get values from select dropdowns
        selects.forEach(select => {
          const optionIndex = parseInt(select.getAttribute('data-option-index'));
          options[optionIndex] = select.value;
        });
        
        return options;
      }
      
      // Function to find matching variant
      function findMatchingVariant() {
        const selectedOptions = getSelectedOptions();
        
        return variants.find(variant => {
          let match = true;
          if (selectedOptions[0] && variant.option1 !== selectedOptions[0]) match = false;
          if (selectedOptions[1] && variant.option2 !== selectedOptions[1]) match = false;
          if (selectedOptions[2] && variant.option3 !== selectedOptions[2]) match = false;
          return match;
        });
      }
      
      // Function to update variant
      function updateVariant(announceChange) {
        const variant = findMatchingVariant();
        
        if (variant) {
          // Update hidden variant ID input
          variantIdInput.value = variant.id;
          const formOwner = form.closest('yise-product-form');
          if (formOwner && !formOwner.syncCartVariantById?.(variant.id) && !formOwner.crossSellReadyBound) {
            formOwner.crossSellReadyBound = true;
            owner.listen(formOwner, 'yise:product-form-ready', () => {
              delete formOwner.crossSellReadyBound;
              updateVariant(false);
            }, { once: true });
          }
          
          let displayPrice = variant.price;
          if (priceElement) {
            const pricing = window.YISETemporarySalePrice && typeof window.YISETemporarySalePrice.resolveVariantPrice === 'function'
              ? window.YISETemporarySalePrice.resolveVariantPrice({
                  price: variant.price,
                  price_cents: variant.price_cents,
                  compareAtPrice: variant.compare_at_price || '',
                  compare_at_price_cents: variant.compare_at_price_cents,
                  temporarySalePrice: variant.temporarySalePrice
                })
              : null;
            displayPrice = pricing && pricing.priceFormatted ? pricing.priceFormatted : variant.price;
            const comparePriceFormatted = (pricing && pricing.compareAtPriceFormatted)
              ? pricing.compareAtPriceFormatted
              : (variant.compare_at_price || null);

            const priceLabel = config.priceLabel;
            const regularPriceLabel = config.regularPriceLabel;
            const salePriceLabel = config.salePriceLabel;
            const isOnSale = !!comparePriceFormatted;
            const currentPriceLabel = isOnSale ? salePriceLabel : priceLabel;
            const displayPriceText = displayPrice.replace('.00', '');
            let priceHTML = `<span class="visually-hidden">${currentPriceLabel} </span>${displayPriceText}`;

            if (isOnSale) {
              const comparePrice = comparePriceFormatted.replace('.00', '');
              priceHTML = `${priceHTML}<span class="visually-hidden">${regularPriceLabel} </span> <span class="pdp-item__price-strikethrough" style="text-decoration: line-through;opacity:0.5;">${comparePrice}</span>`;
            }

            priceElement.innerHTML = priceHTML;
          }
          
          // Update button availability
          if (submitButton) {
            submitButton.disabled = !variant.available;
          }
          
          // Update product image if variant has featured image
          if (productImage && variant.featured_image) {
            productImage.src = variant.featured_image;
          }

          if (announceChange) {
            const productForm = crossSell.closest('yise-product-form[data-template-product]');
            if (typeof productForm?.announce === 'function') {
              const productTitle = item.querySelector('.pdp-cross-sell__title')?.textContent?.trim() || '';
              const variantTitle = variant.title && variant.title !== 'Default Title' ? `${variant.title}. ` : '';
              const availability = variant.available ? 'Available.' : 'Unavailable.';
              productForm.announce(`${productTitle}. ${variantTitle}Price ${displayPrice}. ${availability}`);
            }
          }

          // Update cart form data for pending cart item display
          const cartFormDataScript = form.querySelector('script.cart-form-product-data');
          if (cartFormDataScript) {
            try {
              const cartFormData = JSON.parse(cartFormDataScript.textContent);
              
              // Update variant-specific fields
              const pricing = window.YISETemporarySalePrice && typeof window.YISETemporarySalePrice.resolveVariantPrice === 'function'
                ? window.YISETemporarySalePrice.resolveVariantPrice({
                    price: variant.price,
                    price_cents: variant.price_cents,
                    compareAtPrice: variant.compare_at_price || '',
                    compare_at_price_cents: variant.compare_at_price_cents,
                    temporarySalePrice: variant.temporarySalePrice
                  })
                : null;
              cartFormData.variant_id = variant.id;
              cartFormData.price = pricing && pricing.price ? pricing.price : variant.price_cents;
              cartFormData.compare_at_price = pricing && pricing.compareAtPrice ? pricing.compareAtPrice : null;
              cartFormData.temporary_sale_price = variant.temporarySalePrice || null;
              cartFormData.variant_title = variant.title === 'Default Title' ? '' : variant.title;
              
              // Update image if variant has one
              if (variant.featured_image) {
                cartFormData.image = variant.featured_image;
              }
              
              cartFormDataScript.textContent = JSON.stringify(cartFormData);
            } catch (e) {
              console.error('Failed to update cart form data:', e);
            }
          }
        }
      }
      
      // Add change listeners to all selects
      selects.forEach(select => {
        owner.listen(select, 'change', function () {
          updateVariant(true);
        });
      });
      
      // Add change listeners to all swatch inputs
      swatchInputs.forEach(input => {
        owner.listen(input, 'change', function() {
          // Update the selected swatch text label
          const optionIndex = this.getAttribute('data-option-index');
          const selectedLabel = container.querySelector(`.pdp-cross-sell__swatch-selected[data-product-id="${productId}"][data-option-index="${optionIndex}"]`);
          if (selectedLabel) {
            selectedLabel.textContent = this.value;
          }
          // Update variant
          updateVariant(true);
        });
      });

      // Initialize with first variant
      updateVariant(false);
    });

    // Listen for successful add to cart from cross-sell forms
    const crossSellForms = crossSell.querySelectorAll('form');
    this.crossSellForms = crossSellForms;
    crossSellForms.forEach(form => {
      owner.listen(form, 'submit', function(e) {
        const item = this.closest('.pdp-cross-sell__item');
        if (!item) return;

        const productId = item.querySelector('[data-product-id]')?.getAttribute('data-product-id');
        const variantIdInput = this.querySelector('input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : null;
        const productTitle = item.querySelector('.pdp-cross-sell__title')?.textContent?.trim() || '';

        // Get variant data from JSON script
        let variantTitle = '';
        let variantPrice = 0;
        const variantsScript = item.querySelector(`script[data-product-variants="${productId}"]`);
        if (variantsScript && variantId) {
          try {
            const variants = JSON.parse(variantsScript.textContent);
            const matchingVariant = variants.find(v => String(v.id) === String(variantId));
            if (matchingVariant) {
              variantTitle = matchingVariant.title === 'Default Title' ? '' : matchingVariant.title;
              const pricing = window.YISETemporarySalePrice && typeof window.YISETemporarySalePrice.resolveVariantPrice === 'function'
                ? window.YISETemporarySalePrice.resolveVariantPrice({
                    price: matchingVariant.price,
                    price_cents: matchingVariant.price_cents,
                    compareAtPrice: matchingVariant.compare_at_price || '',
                    compare_at_price_cents: matchingVariant.compare_at_price_cents,
                    temporarySalePrice: matchingVariant.temporarySalePrice
                  })
                : null;
              variantPrice = pricing && pricing.price ? pricing.price : (matchingVariant.price_cents || 0);
            }
          } catch (err) {
            console.error('Failed to parse variant data:', err);
          }
        }

        // Store the data for dispatching after successful add
        form._crossSellData = {
          productTitle: productTitle,
          productId: productId,
          variantTitle: variantTitle,
          variantId: variantId,
          price: variantPrice
        };
      });
    });

    // Listen for cart update events to dispatch cross-sell added event
    owner.listen(document, 'cart:updated', function(e) {
      // Check if there's a pending cross-sell add
      crossSellForms.forEach(form => {
        if (form._crossSellData) {
          const data = form._crossSellData;
          form._crossSellData = null;

          // Dispatch custom event for Google Analytics tracking
          const crossSellEvent = new CustomEvent('cross-sell:added', {
            bubbles: true,
            detail: {
              productTitle: data.productTitle,
              productId: data.productId,
              variantTitle: data.variantTitle,
              variantId: data.variantId,
              price: data.price,
              source: 'PDP Cross Sell'
            }
          });
          document.dispatchEvent(crossSellEvent);
        }
      });
    });
  }

  waitForMarkup() {
    const signal = this.abortController?.signal;
    if (this.markupObserver || !signal || signal.aborted || typeof MutationObserver === 'undefined') return;
    this.markupObserver = new MutationObserver(() => {
      if (!signal.aborted && this.abortController?.signal === signal) this.initialize();
    });
    this.markupObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  teardown() {
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.initialized = false;
    this.crossSellForms?.forEach((form) => {
      delete form._crossSellData;
      const formOwner = form.closest('yise-product-form');
      if (formOwner) delete formOwner.crossSellReadyBound;
    });
    this.crossSellForms = null;
  }
}

defineElement('yise-cross-sell', YiseCrossSell);
